import { MdOutlineArrowDownward } from "react-icons/md";
import { formatNumber, formatPercent } from "./crmHelpers";

/*
  O funil propriamente dito.

  Desenhado em CSS, não num gráfico de biblioteca, por três razões:

  1. **A forma precisa estreitar.** A versão anterior eram quatro barras
     alinhadas à esquerda sobre trilhos iguais — isso é um gráfico de barras,
     não um funil, e não comunica estreitamento. Aqui cada degrau é centrado e
     tem largura proporcional ao anterior.

  2. **A perda ENTRE os degraus é a informação principal**, e ela não é um
     degrau: mora no espaço entre dois. Numa biblioteca de gráficos isso vira
     anotação frágil; em CSS é um elemento próprio.

  3. **O maior vazamento precisa se destacar.** Saber que o funil perde 58% na
     conversão vale mais do que os quatro números somados, e é o que decide
     onde a equipe mexe primeiro.
*/

/*
  Largura mínima do degrau.

  Um funil que converte 9% teria o último degrau com 9% da largura — e o
  rótulo não caberia. O piso garante que o NOME do degrau continue legível; a
  proporção real continua dita pelo número e pela porcentagem ao lado, que não
  são afetados pelo piso.
*/
const LARGURA_MINIMA = 46;

const FunnelShape = ({ steps }) => {
  if (!steps?.length) return null;

  const total = steps[0].count || 1;

  // O degrau com a maior perda relativa ganha destaque. Em empate, o primeiro
  // — que é o mais cedo no funil e o mais barato de corrigir.
  const maiorVazamento = steps.reduce(
    (pior, step, index) =>
      index > 0 && step.dropRateFromPrevious > (pior?.taxa ?? -1)
        ? { index, taxa: step.dropRateFromPrevious }
        : pior,
    null
  );

  return (
    <div className="px-4 py-5">
      {steps.map((step, index) => {
        const largura = Math.max((step.count / total) * 100, LARGURA_MINIMA);
        const destaque = maiorVazamento?.index === index;

        return (
          <div key={step.id}>
            {index > 0 ? (
              <div
                className={`flex items-center justify-center gap-1.5 py-1.5 text-xs ${
                  destaque ? "font-semibold text-rose-600" : "text-slate-400"
                }`}
              >
                <MdOutlineArrowDownward size={13} />
                <span>
                  −{formatNumber(step.dropFromPrevious)} (
                  {formatPercent(step.dropRateFromPrevious)})
                </span>
                {destaque ? <span>· maior perda do funil</span> : null}
              </div>
            ) : null}

            <div
              className={`mx-auto flex items-center justify-between gap-3 rounded-xl px-4 py-3 transition-all ${
                index === 0
                  ? "bg-slate-900 text-white"
                  : destaque
                  ? "bg-rose-50 text-slate-900 ring-1 ring-inset ring-rose-200"
                  : "bg-slate-100 text-slate-900"
              }`}
              style={{ width: `${largura}%` }}
            >
              <span className="min-w-0 text-sm font-semibold leading-tight">
                {step.label}
              </span>
              <span className="flex shrink-0 items-baseline gap-1.5">
                <span className="text-lg font-bold leading-none">
                  {formatNumber(step.count)}
                </span>
                <span
                  className={`text-[11px] ${
                    index === 0 ? "text-white/60" : "text-slate-500"
                  }`}
                >
                  {formatPercent(step.shareOfTotal)}
                </span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FunnelShape;
